import { lazy, Suspense } from 'react'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import SplitShell from '@/components/SplitShell'
import NotFound from '@/components/NotFound'
import AppLayout from '@/layouts/AppLayout'
import RouteEnforcer from '@/components/RouteEnforcer'
import UpdateBanner from '@/components/UpdateBanner'
import CacheStatusBar from '@/components/CacheStatusBar'
import { ToastContainer } from '@/components/ui'
import AppLoading from '@/components/AppLoading'

// Standalone
const Login = lazy(() => import('@/pages/auth/Login'))
const Onboard = lazy(() => import('@/pages/onboard/Onboard'))
const ChangePassword = lazy(() => import('@/pages/auth/ChangePassword'))
const FireAdminDashboard = lazy(() => import('@/pages/fire-admin/FireAdminDashboard'))

// Roster
const RosterList = lazy(() => import('@/pages/roster/RosterList'))
const EmployeeDetail = lazy(() => import('@/pages/roster/EmployeeDetail'))
const NewEmployee = lazy(() => import('@/pages/roster/NewEmployee'))
const HRCredentials = lazy(() => import('@/pages/roster/HRCredentials'))
const PayRates = lazy(() => import('@/pages/roster/PayRates'))

// Units
const UnitsList = lazy(() => import('@/pages/units/UnitsList'))
const UnitDetail = lazy(() => import('@/pages/units/UnitDetail'))
const NewUnit = lazy(() => import('@/pages/units/NewUnit'))

// Incidents
const IncidentsList = lazy(() => import('@/pages/incidents/IncidentsList'))
const IncidentDetail = lazy(() => import('@/pages/incidents/IncidentDetail'))
const NewIncident = lazy(() => import('@/pages/incidents/NewIncident'))
const ShiftTicket = lazy(() => import('@/pages/incidents/ShiftTicket'))

// Encounters
const EncounterDetail = lazy(() => import('@/pages/encounters/EncounterDetail'))
const EncountersList = lazy(() => import('@/pages/encounters/EncountersList'))
const NewEncounter = lazy(() => import('@/pages/encounters/NewEncounter'))
const NewSimpleEncounter = lazy(() => import('@/pages/encounters/NewSimpleEncounter'))
const NewPCREncounter = lazy(() => import('@/pages/encounters/NewPCREncounter'))
const NewPhoto = lazy(() => import('@/pages/encounters/NewPhoto'))
const NewProcedure = lazy(() => import('@/pages/encounters/NewProcedure'))
const EncounterEdit = lazy(() => import('@/pages/encounters/EncounterEdit'))
const UnsignedItems = lazy(() => import('@/pages/unsigned-items/UnsignedItems'))
const PatientSearch = lazy(() => import('@/pages/encounters/PatientSearch'))

// MAR
const MARList = lazy(() => import('@/pages/mar/MARList'))
const MARDetail = lazy(() => import('@/pages/mar/MARDetail'))
const MARSearch = lazy(() => import('@/pages/mar/MARSearch'))
const MARNew = lazy(() => import('@/pages/mar/MARNew'))

// CS
const CSOverview = lazy(() => import('@/pages/cs/CSOverview'))
const CSList = lazy(() => import('@/pages/cs/CSList'))
const CSItemDetail = lazy(() => import('@/pages/cs/CSItemDetail'))
const CSCount = lazy(() => import('@/pages/cs/CSCount'))
const CSTransfer = lazy(() => import('@/pages/cs/CSTransfer'))
const CSReceive = lazy(() => import('@/pages/cs/CSReceive'))
const CSChecklist = lazy(() => import('@/pages/cs/CSChecklist'))
const CSInventoryCount = lazy(() => import('@/pages/cs/CSInventoryCount'))
const CSAudit = lazy(() => import('@/pages/cs/CSAudit'))

// Inventory
const InventoryList = lazy(() => import('@/pages/inventory/InventoryList'))
const InventoryDetail = lazy(() => import('@/pages/inventory/InventoryDetail'))
const InventoryAdd = lazy(() => import('@/pages/inventory/InventoryAdd'))
const ExpirationDashboard = lazy(() => import('@/pages/inventory/ExpirationDashboard'))
const Reorder = lazy(() => import('@/pages/inventory/Reorder'))
const BurnRate = lazy(() => import('@/pages/inventory/BurnRate'))
const Formulary = lazy(() => import('@/pages/formulary/Formulary'))
const FormularyDetail = lazy(() => import('@/pages/formulary/FormularyDetail'))
const CatalogList = lazy(() => import('@/pages/catalog/CatalogList'))
const CatalogDetail = lazy(() => import('@/pages/catalog/CatalogDetail'))

// Supply Runs
const SupplyRunsList = lazy(() => import('@/pages/supply-runs/SupplyRunsList'))
const SupplyRunDetail = lazy(() => import('@/pages/supply-runs/SupplyRunDetail'))
const NewSupplyRun = lazy(() => import('@/pages/supply-runs/NewSupplyRun'))

// ICS 214
const ICS214List = lazy(() => import('@/pages/ics214/ICS214List'))
const ICS214Detail = lazy(() => import('@/pages/ics214/ICS214Detail'))
const ICS214Activity = lazy(() => import('@/pages/ics214/ICS214Activity'))
const NewICS214 = lazy(() => import('@/pages/ics214/NewICS214'))

// Schedule
const Schedule = lazy(() => import('@/pages/schedule/Schedule'))
const ScheduleCalendar = lazy(() => import('@/pages/schedule/ScheduleCalendar'))
const GenerateSchedule = lazy(() => import('@/pages/schedule/GenerateSchedule'))

// Other
const Profile = lazy(() => import('@/pages/profile/Profile'))
const ChatPage = lazy(() => import('@/pages/chat/Chat'))
const NotificationsInbox = lazy(() => import('@/pages/notifications/NotificationsInbox'))
const MyPayroll = lazy(() => import('@/pages/payroll/MyPayroll'))
const Payroll = lazy(() => import('@/pages/payroll/Payroll'))
const RootRedirect = lazy(() => import('@/pages/RootRedirect'))
const MyUnit = lazy(() => import('@/pages/dashboard/MyUnit'))
const AMAConsent = lazy(() => import('@/pages/consent/AMAConsent'))
const ConsentToTreat = lazy(() => import('@/pages/consent/ConsentToTreat'))
const NewCompClaim = lazy(() => import('@/pages/comp-claims/NewCompClaim'))
const CompClaimsList = lazy(() => import('@/pages/comp-claims/CompClaimsList'))
const GlobalMap = lazy(() => import('@/pages/map/GlobalMap'))
const Analytics = lazy(() => import('@/pages/analytics/Analytics'))
const Billing = lazy(() => import('@/pages/billing/Billing'))
const Contacts = lazy(() => import('@/pages/contacts/Contacts'))
const Handbook = lazy(() => import('@/pages/documents/Handbook'))
const NewDocument = lazy(() => import('@/pages/documents/NewDocument'))
const DocumentsList = lazy(() => import('@/pages/documents/DocumentsList'))
const DocumentDetail = lazy(() => import('@/pages/documents/DocumentDetail'))

// Admin
const Admin = lazy(() => import('@/pages/admin/Admin'))
const Announcements = lazy(() => import('@/pages/admin/Announcements'))
const PushNotifications = lazy(() => import('@/pages/admin/PushNotifications'))
const ChatRequests = lazy(() => import('@/pages/admin/ChatRequests'))
const Company = lazy(() => import('@/pages/admin/Company'))
const FireDashboard = lazy(() => import('@/pages/admin/FireDashboard'))
const Financial = lazy(() => import('@/pages/admin/Financial'))
const RoleManagement = lazy(() => import('@/pages/admin/RoleManagement'))

function App() {
  return (
    <BrowserRouter>
      <ToastContainer />
      {/* SW update banner — outside Routes so the SW registers on first load regardless of route or auth state */}
      <UpdateBanner />
      <CacheStatusBar />
      <RouteErrorBoundary>
        <Suspense fallback={<AppLoading />}>
          <Routes>
            {/* ── Authentication/Permissions Enforcement ── */}
            <Route element={<RouteEnforcer />}>

              {/* ── Standalone ── */}
              <Route path="/login" element={<Login />} />
              <Route path="/onboard" element={<Onboard />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/fire-admin/:code" element={<FireAdminDashboard />} />

              {/* ── App layout ── */}
              <Route path="/" element={<AppLayout />}>
                {/* Root — redirects to user home */}
                <Route index element={<RootRedirect />} />

                {/* Roster */}
                <Route path="roster" element={<SplitShell basePath="/roster"><RosterList /></SplitShell>}>
                  <Route path=":id" element={<EmployeeDetail />} />
                </Route>
                <Route path="roster/new" element={<NewEmployee />} />
                <Route path="roster/hr" element={<HRCredentials />} />
                <Route path="roster/pay-rates" element={<PayRates />} />

                {/* Units */}
                <Route path="units" element={<SplitShell basePath="/units" listWidth="lg:w-1/2"><UnitsList /></SplitShell>}>
                  <Route path=":id" element={<UnitDetail />} />
                </Route>
                <Route path="units/new" element={<NewUnit />} />

                {/* Incidents */}
                <Route path="incidents" element={<IncidentsList />} />
                <Route path="incidents/:id" element={<IncidentDetail />} />
                <Route path="incidents/new" element={<NewIncident />} />
                <Route path="incidents/:id/shift-ticket" element={<ShiftTicket />} />

                {/* Encounters */}
                <Route path="encounters" element={<SplitShell basePath="/encounters"><EncountersList /></SplitShell>}>
                  <Route path=":id" element={<EncounterDetail />} />
                </Route>
                <Route path="encounters/new" element={<NewEncounter />} />
                <Route path="encounters/new/simple" element={<NewSimpleEncounter />} />
                <Route path="encounters/new/pcr" element={<NewPCREncounter />} />
                <Route path="encounters/photos/new" element={<NewPhoto />} />
                <Route path="encounters/procedures/new" element={<NewProcedure />} />
                <Route path="encounters/:id/edit" element={<EncounterEdit />} />
                <Route path="unsigned-items" element={<UnsignedItems />} />
                <Route path="unsigned-orders" element={<Navigate to="/unsigned-items" replace />} />
                <Route path="unsigned-pcrs" element={<Navigate to="/unsigned-items" replace />} />
                <Route path="patient-search" element={<PatientSearch />} />

                {/* MAR */}
                <Route path="mar" element={<SplitShell basePath="/mar"><MARList /></SplitShell>}>
                  <Route path=":id" element={<MARDetail />} />
                </Route>
                <Route path="mar/search" element={<MARSearch />} />
                <Route path="mar/new" element={<MARNew />} />

                {/* CS */}
                <Route path="cs/overview" element={<CSOverview />} />
                <Route path="cs" element={<SplitShell basePath="/cs" detailPattern="/cs/item/:id"><CSList /></SplitShell>}>
                  <Route path="item/:id" element={<CSItemDetail />} />
                </Route>
                <Route path="cs/count" element={<CSCount />} />
                <Route path="cs/transfer" element={<CSTransfer />} />
                <Route path="cs/receive" element={<CSReceive />} />
                <Route path="cs/checklist" element={<CSChecklist />} />
                <Route path="cs-inventory/count" element={<CSInventoryCount />} />
                <Route path="cs/audit" element={<CSAudit />} />

                {/* Inventory */}
                <Route path="inventory" element={<SplitShell basePath="/inventory"><InventoryList /></SplitShell>}>
                  <Route path=":id" element={<InventoryDetail />} />
                </Route>
                <Route path="inventory/add" element={<InventoryAdd />} />
                <Route path="inventory/expiring" element={<ExpirationDashboard />} />
                <Route path="inventory/reorder" element={<Reorder />} />
                <Route path="inventory/burnrate" element={<BurnRate />} />
                <Route path="formulary" element={<SplitShell basePath="/formulary"><Formulary /></SplitShell>}>
                  <Route path=":id" element={<FormularyDetail />} />
                </Route>
                <Route path="catalog" element={<SplitShell basePath="/catalog"><CatalogList /></SplitShell>}>
                  <Route path=":id" element={<CatalogDetail />} />
                </Route>

                {/* Supply Runs */}
                <Route path="supply-runs" element={<SplitShell basePath="/supply-runs"><SupplyRunsList /></SplitShell>}>
                  <Route path=":id" element={<SupplyRunDetail />} />
                </Route>
                <Route path="supply-runs/new" element={<NewSupplyRun />} />

                {/* ICS 214 */}
                <Route path="ics214" element={<ICS214List />} />
                <Route path="ics214/:id" element={<ICS214Detail />} />
                <Route path="ics214/:id/activity" element={<ICS214Activity />} />
                <Route path="ics214/new" element={<NewICS214 />} />

                {/* Schedule */}
                <Route path="schedule" element={<Schedule />} />
                <Route path="schedule/request" element={<Schedule />} />
                <Route path="schedule/calendar" element={<ScheduleCalendar />} />
                <Route path="schedule/generate" element={<GenerateSchedule />} />

                {/* Other */}
                <Route path="profile" element={<Profile />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="notifications" element={<NotificationsInbox />} />
                <Route path="payroll/my" element={<MyPayroll />} />
                <Route path="payroll" element={<Payroll />} />
                <Route path="dashboard/my-unit" element={<MyUnit />} />
                <Route path="consent/ama" element={<AMAConsent />} />
                <Route path="consent/treat" element={<ConsentToTreat />} />
                <Route path="comp-claims/new" element={<NewCompClaim />} />
                <Route path="comp-claims" element={<CompClaimsList />} />
                <Route path="map" element={<GlobalMap />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="billing" element={<Billing />} />
                <Route path="contacts" element={<Contacts />} />
                <Route path="documents/handbook" element={<Handbook />} />
                <Route path="documents/new" element={<NewDocument />} />
                <Route path="documents" element={<SplitShell basePath="/documents"><DocumentsList /></SplitShell>}>
                  <Route path=":id" element={<DocumentDetail />} />
                </Route>

                {/* Admin */}
                <Route path="admin" element={<Admin />} />
                <Route path="admin/announcements" element={<Announcements />} />
                <Route path="admin/push-notifications" element={<PushNotifications />} />
                <Route path="admin/chat-requests" element={<ChatRequests />} />
                <Route path="admin/company" element={<Company />} />
                <Route path="admin/fire-dashboard" element={<FireDashboard />} />
                <Route path="admin/financial" element={<Financial />} />
                <Route path="admin/roles" element={<RoleManagement />} />

              </Route> {/* AppLayout */}
            </Route> {/* RouteEnforcer */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </BrowserRouter>
  )
}

export default App
